import React from 'react';
import { ForgeIcon } from '@tylertech/forge-react';

import { I18n } from 'common/i18n';
import {
  ForgeStatusIcons,
  type StatusValues,
  type STATUS_ENDED,
} from 'common/performance_measures/lib/constants';

interface StatusIconProps {
  value: StatusValues | typeof STATUS_ENDED;
}

/**
 * checks that we are not trying to render an icon for 'ended' status.
 * the StatusBanner component is potentially passing 'ended' status to
 * StatusIcon but StatusIcon doesn't handle this case because no such value
 * is defined in ForgeStatusIcons.
 */
const StatusIcon = ({ value }: StatusIconProps) => {
  const icon = ForgeStatusIcons[value as keyof typeof ForgeStatusIcons];
  return icon ? <ForgeIcon name={icon} /> : null;
};

export interface StatusBannerProps {
  ended?: boolean;
  /** overrides specific to each possible value */
  labels?: Partial<Record<StatusValues, string>> & { ended?: string };
  /** specifies the color, icon, and default label. If not provided, the status banner is disabled. */
  value?: StatusValues | typeof STATUS_ENDED;
}

/**
 * Creates a status banner for the Measure Result Card.
 *
 * Status is user configured as part of measure creation.
 */
export const StatusBanner = ({ ended, value, labels }: StatusBannerProps) => {
  if (!value) {
    return null;
  }

  const labelOverride = labels?.[value as StatusValues];
  const labelDefault = I18n.t(`shared.performance_measures.measure.status.values.${value}`);
  const label = ended ? labels?.ended : labelOverride || labelDefault;

  return (
    <div className={`status-banner status-banner-${value}`}>
      <div className="status-banner-content">
        <StatusIcon value={value} />
        <span className="status-banner-text" title={label}>
          {label}
        </span>
      </div>
      <div className="flag-ends"></div>
    </div>
  );
};
